import { useState } from 'react'
import { signIn, signUp } from '../lib/auth.js'

/**
 * Sign-in gate. Defaults to signing in, because that is what he does every
 * time after the first. Creating the account is tucked behind a link so the
 * common path is one screen, two fields, one button.
 */
export default function SignIn() {
  const [mode, setMode] = useState('in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null)

  const creating = mode === 'up'

  async function submit(e) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      if (creating) {
        const { needsConfirmation } = await signUp(email, password)
        if (needsConfirmation) {
          setNote('Account made. Check ' + email.trim() + ' and click the confirm link, then sign in.')
          setMode('in')
        }
        /* With confirmation off, the session arrives and App swaps the screen. */
      } else {
        await signIn(email, password)
      }
    } catch (err) {
      const raw = err.message || ''
      setError(
        /failed to fetch|network/i.test(raw)
          ? 'Could not reach the database. Check your connection and try again.'
          : raw || 'That did not work.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="main auth">
      <div className="auth-head">
        <span className="lbl">MyPersonalOS</span>
        <h1 className="serif">{creating ? 'Make your account' : 'Sign in'}</h1>
        <p>
          {creating
            ? 'One account, yours. Nothing here is shared with anyone.'
            : 'Once on this phone. It stays signed in after that.'}
        </p>
      </div>

      <form
        className="card"
        onSubmit={submit}
        /* A form that will not submit must never look like a broken button. */
        onInvalid={() => setError('Check the email and password fields above.')}
      >
        <div className="pad">
          <div className="field">
            <label className="lbl" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="lbl" htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              autoComplete={creating ? 'new-password' : 'current-password'}
              /* Only when making one. A length rule on the way in refuses to
                 submit a password that is already the account's, and the
                 browser blocks it so quietly that the screen simply does
                 nothing when you press the button. */
              minLength={creating ? 8 : undefined}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error ? <p className="auth-msg bad">{error}</p> : null}
          {note ? <p className="auth-msg ok">{note}</p> : null}

          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Working…' : creating ? 'Create account' : 'Sign in'}
          </button>
        </div>
      </form>

      <button
        type="button"
        className="auth-switch"
        onClick={() => {
          setMode(creating ? 'in' : 'up')
          setError(null)
          setNote(null)
        }}
      >
        {creating ? 'I already have an account' : 'First time? Make the account'}
      </button>
    </div>
  )
}
